// src/components/Board/AiDiagnosisPanel.tsx
import React, { useEffect, useState, useMemo } from 'react';
import { useProjectContext } from '../../contexts/ProjectContext';
import { LlmDiagnosis, DiagnosisId, LlmDiagnosisItem } from '../../types/llm-diagnosis';
import {
  diagnosisStatusLabel,
  getSeverityClass,
  getSeverityLabel,
  isActionableDiagnosis,
} from '../../utils/diagnosis';
import { formatDate } from '../../utils/date';
import styles from '../../assets/styles/components/AiDiagnosisPanel.module.scss';

type QuickSendPayload = {
  text: string;
  periodDays?: number | null;
  diagnosisId?: DiagnosisId | null;
};

interface AiDiagnosisPanelProps {
  onQuickSend: (payload: QuickSendPayload) => void;
  activeDiagnosisId: DiagnosisId | null;
  setActiveDiagnosisId: (id: DiagnosisId | null) => void;
}

const normalizeDiagnosisResponse = (raw: Record<string, unknown>): LlmDiagnosis => {
  if (!raw || typeof raw !== 'object') return {};
  const normalized: Record<string, unknown> = {};
  Object.entries(raw).forEach(([key, value]) => {
    if (value && typeof value === 'object' && 'flag' in value) {
      const diagnosisVal = value as Record<string, unknown>;
      normalized[key] = {
        ...diagnosisVal,
        label: (diagnosisVal.label as string) ?? '',
        flag: diagnosisVal.flag,
      };
      return;
    }
    normalized[key] = value;
  });
  return normalized as LlmDiagnosis;
};

const getDiagnosisUpdatedAt = (item: LlmDiagnosisItem | null | undefined): string | null => {
  if (!item) return null;
  return item.diagnosis_generated_at ?? item.updated_at ?? null;
};

const AiDiagnosisPanel: React.FC<AiDiagnosisPanelProps> = ({
  onQuickSend,
  activeDiagnosisId,
  setActiveDiagnosisId,
}) => {
  const { selectedProjectId } = useProjectContext();
  const [llmDiagnosis, setLlmDiagnosis] = useState<LlmDiagnosis | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // フィルター用ステート
  const [filter, setFilter] = useState<'all' | 'high' | 'medium' | 'low'>('all');

  // Fetch diagnosis data
  useEffect(() => {
    const fetchDiagnosis = async (projectId: number) => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/get-diagnosis?project_id=${projectId}`,
          { credentials: 'include' }
        );
        if (!res.ok) throw new Error('診断データの取得に失敗しました。');
        const data = await res.json();
        setLlmDiagnosis(normalizeDiagnosisResponse(data));
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    };
    if (selectedProjectId) {
      fetchDiagnosis(selectedProjectId);
    } else {
      setLlmDiagnosis(null);
    }
  }, [selectedProjectId]);

  const diagnosisItems = useMemo(() => {
    if (!llmDiagnosis) return [];
    return Object.entries(llmDiagnosis)
      .filter(([_, d]) => d !== null)
      .map(([id, d]) => ({
        id: id as DiagnosisId,
        label: d?.label ?? '未設定',
        flag: d?.flag ?? null,
        ...d,
      }));
  }, [llmDiagnosis]);

  const latestDiagnosisUpdatedAt = useMemo(() => {
    if (!llmDiagnosis) return null;

    return Object.values(llmDiagnosis).reduce<string | null>((latest, item) => {
      const candidate = getDiagnosisUpdatedAt(item);
      if (!candidate) return latest;
      if (!latest) return candidate;

      return new Date(candidate).getTime() > new Date(latest).getTime() ? candidate : latest;
    }, null);
  }, [llmDiagnosis]);

  // フィルター絞り込みロジック
  const filteredItems = useMemo(() => {
    return diagnosisItems.filter((item) => {
      if (filter === 'all') return true;
      const flag = item.flag;
      if (filter === 'high') return flag === 'high';
      if (filter === 'medium') return flag === 'medium';
      if (filter === 'low') return flag === 'low' || !flag; // 未設定はlow扱い
      return true;
    });
  }, [diagnosisItems, filter]);

  const handleItemClick = (id: DiagnosisId) => {
    if (activeDiagnosisId === id) {
      setActiveDiagnosisId(null);
    } else {
      setActiveDiagnosisId(id);
    }
  };

  const renderActiveDetail = (item: any) => {
    const reason = item.task_details?.reason || 'データ分析結果に基づき自動検出されました。';
    const efficacy = item.task_details?.efficacy || '';
    const behavior = item.task_details?.auto_fix_behavior || '';
    const canCreateSuggestion = isActionableDiagnosis(item.id);

    const suggestionAnalyzeText = (question: string) => {
      return `診断された課題「${item.label}」について、\n${question}`;
    };

    const isHigh = item.flag === 'high';

    return (
      <div className={styles.diagnosisDetailCard} onClick={(e) => e.stopPropagation()}>
        <h4 className={styles.detailTitle}>診断詳細</h4>

        <div className={styles.metaInfo}>
          <div>
            <strong>重要度:</strong>{' '}
            <span className={isHigh ? styles.metaHigh : styles.metaMedium}>{item.flag ?? '-'}</span>
          </div>
          <div>
            <strong>対象媒体:</strong> {item.platform || '-'}
          </div>
        </div>

        <div className={styles.textSection}>
          <div className={styles.sectionLabel}>診断内容</div>
          <p>{reason}</p>
          {efficacy && <p>{efficacy}</p>}
          {behavior && <p>{behavior}</p>}
        </div>

        <div>
          <button
            type="button"
            className={styles.actionButton}
            disabled={!canCreateSuggestion}
            title={canCreateSuggestion ? undefined : 'この診断はAI提案による操作に対応していません'}
            onClick={() =>
              onQuickSend({
                text: suggestionAnalyzeText('この問題を自動修正するための提案を出してください。'),
                periodDays: item.period_value,
                diagnosisId: activeDiagnosisId,
              })
            }
          >
            提案を作成する
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.diagnosisPanel}>
      {/* 右揃えで日時を表示 */}
      <div className={styles.panelHeader}>
        <span>AI診断</span>
        <span className={styles.updateTime}>
          {latestDiagnosisUpdatedAt ? `${formatDate(latestDiagnosisUpdatedAt)}更新` : '-'}
        </span>
      </div>

      {/* 診断項目の絞り込み用タブ */}
      <div className={styles.diagnosisFilterBar}>
        <button
          type="button"
          className={`${styles.filterTab} ${filter === 'all' ? styles.active : ''}`}
          onClick={() => setFilter('all')}
        >
          すべて
        </button>
        <button
          type="button"
          className={`${styles.filterTab} ${filter === 'high' ? styles.active : ''}`}
          onClick={() => setFilter('high')}
        >
          緊急
        </button>
        <button
          type="button"
          className={`${styles.filterTab} ${filter === 'medium' ? styles.active : ''}`}
          onClick={() => setFilter('medium')}
        >
          注意
        </button>
        <button
          type="button"
          className={`${styles.filterTab} ${filter === 'low' ? styles.active : ''}`}
          onClick={() => setFilter('low')}
        >
          確認
        </button>
      </div>

      <div className={styles.panelContent}>
        {loading && <div className={styles.loadingText}>診断項目を読み込み中...</div>}
        {error && <div className={styles.errorText}>{error}</div>}

        {!loading && !error && filteredItems.length === 0 && (
          <div className={styles.emptyText}>指摘されている診断項目はありません。</div>
        )}

        <div className={styles.diagnosisList}>
          {filteredItems.map((item) => {
            const isActive = activeDiagnosisId === item.id;
            const severityClass = getSeverityClass(item.flag);
            return (
              <div key={item.id}>
                <div
                  className={`${styles.diagnosisCard} ${isActive ? styles.active : ''} ${styles[severityClass]}`}
                  onClick={() => handleItemClick(item.id)}
                >
                  <div className={styles.diagnosisCardHeader}>
                    <span className={styles.diagnosisTitle}>{item.label}</span>
                    {item.flag && (
                      <span className={`${styles.flagBadge} ${styles[severityClass]}`}>
                        {getSeverityLabel(item.flag)}
                      </span>
                    )}
                  </div>
                  {isActive && renderActiveDetail(item)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AiDiagnosisPanel;
